import {
  DataType,
  Form,
  FormOptions,
  Section,
  SelectOption,
  useNotify
} from '@globaleris/ui.library'
import { Button, Card, Grid } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { EnumCommodity, EnumCurrency, EnumTipoCalculoConcepto } from '../../../api'
import { EnumClientTypes } from '../../../api/Argentia/enums/enumClientTypes'
import { EnumConceptType } from '../../../api/Argentia/enums/enumConceptType'
import { LanguageModel } from '../../../api/Argentia/model/contracts'
import Page, { PageLayout } from '../../../components/page/page'
import PageHeader from '../../../components/page/pageHeader'
import { enumEnumToValueNumber, enumToSelectOptionArray } from '../../../helpers'
import { missingFieldsForm } from '../../../helpers/validateFormHelper'
import { useDropdownOptions } from '../../../hooks/useDropdownOptions'
import { mapLanguagesResponse } from '../../../mappers/language.mapper'
import { NonAuthRoutes } from '../../../model'
import { getActiveCommodities, getActiveCompany, getCustomerDomain } from '../../../redux'
import { fetchLanguages, getDefaultLanguage } from '../../../services/languages.service'
import { mapFormAddDataToConceptRequest } from '../concepts.mapper'
import { createConcept, fetchConceptTypesByClientType, fetchIVATypes } from '../concepts.service'

export interface ConceptAddPageProps {
  location: { state: { clientType: EnumClientTypes } }
}

export const ConceptsAddPage: React.FC<ConceptAddPageProps> = props => {
  const history = useHistory()
  const clientType = props.location?.state?.clientType ?? EnumClientTypes.FINAL_CLIENT

  const [t] = useTranslation(['globalerisLibrary', 'concepts', 'enums'])
  const { info, error } = useNotify()
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [saveLoading, setSaveLoading] = useState<boolean>(false)
  const [conceptData, setConceptData] = useState<any>({})
  const [conceptTypeSelected, setConceptTypeSelected] = useState<EnumConceptType>()
  const [languages, setLanguages] = useState<LanguageModel[]>([])
  const {
    isLoading: conceptTypesLoading,
    responseData: conceptTypesOption,
    responseError: conceptTypesError,
    fetchData: fetchDataConceptTypes
  } = useDropdownOptions<SelectOption, Parameters<typeof fetchConceptTypesByClientType>>(
    fetchConceptTypesByClientType
  )
  const {
    isLoading: IVAtypeLoading,
    responseData: IVAtypeOptions,
    responseError: IVAtypeError,
    fetchData: fetchDataIVAtypes
  } = useDropdownOptions<SelectOption, Parameters<typeof fetchIVATypes>>(fetchIVATypes)

  const commodities = getActiveCommodities()
  const company = getActiveCompany()

  // #region Handlers Starts
  const handleSaveAndRedirect = async (data, redirect): Promise<boolean> => {
    setSaveLoading(true)
    let preventFromRouting: boolean = false
    let conceptId: number | undefined = undefined

    if (missingFieldsForm(addFormSections, data)) {
      error(t('concepts:forms.add.notifications.save.errorInvalidForm'))
      return false
    } else {
      await getDefaultLanguage()
        .then(async defaultLanguage => {
          if (defaultLanguage) {
            const newConcept = mapFormAddDataToConceptRequest(data, defaultLanguage, company?.id)
            await createConcept(newConcept).then(async response => {
              if (response.success) {
                info(t('concepts:forms.add.notifications.save.success'))
                preventFromRouting = false
                conceptId = response.data?.id
              } else {
                error(t('concepts:forms.add.notifications.save.error'))
                preventFromRouting = true
              }
            })
          } else {
            error(t('concepts:forms.add.notifications.save.errorDefaultLanguage'))
            preventFromRouting = true
          }
        })
        .finally(() => {
          setSaveLoading(false)
        })

      if (!preventFromRouting) redirect(conceptId) // it's important to handle the setSaveLoading after routing to ConceptPage, to prevent seting a state after component Unmount
      return true
    }
  }
  // #endregion Handlers Ends

  // #region Functions Starts
  // #endregion Functions Ends

  // #region Form Definition Starts
  const options: FormOptions = {
    elevation: 1,
    columns: 4,
    analytics: {
      customer: getCustomerDomain(),
      view: t('concepts:forms.add.analytics')
    }
  }

  const addFormSections: Section[] = [
    {
      title: t('concepts:forms.add.title'),
      inputs: [
        {
          name: 'code',
          label: t('concepts:forms.add.inputs.code'),
          type: DataType.TEXT,
          required: true
        },
        {
          name: 'description',
          label: t('concepts:forms.add.inputs.description'),
          type: DataType.TEXT,
          required: true
        },
        {
          name: 'conceptType',
          label: t('concepts:forms.add.inputs.conceptType'),
          selectOptions: conceptTypesOption,
          isLoading: conceptTypesLoading,
          type: DataType.SELECT,
          onChange: value => {
            setConceptData(prevState => ({
              ...prevState,
              IVAtype: '0'
            }))
            fetchDataIVAtypes(clientType, Number(value))
          },
          required: true
        },
        {
          name: 'calculationType',
          label: t('concepts:forms.add.inputs.calculationType'),
          selectOptions: enumToSelectOptionArray(
            EnumTipoCalculoConcepto,
            'enums:EnumTipoCalculoConcepto'
          ),
          type: DataType.SELECT,
          required: true
        },
        {
          name: 'IVAtype',
          label: t('concepts:forms.add.inputs.IVAtype'),
          type: DataType.SELECT,
          isLoading: IVAtypeLoading,
          selectOptions: [
            { value: 0, label: t('concepts:forms.add.misc.IVAdefault') },
            ...IVAtypeOptions
          ],
          required: true,
          inputSize: 4,
          defaultValue: '0'
        },
        {
          name: 'currency',
          label: t('concepts:forms.add.inputs.currency'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumCurrency, 'enums:EnumCurrencyLabel'),
          defaultValue: EnumCurrency.EUR,
          required: true,
          inputSize: 4
        },
        {
          name: 'commodity',
          label: t('concepts:forms.add.inputs.commodity'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumCommodity, 'enums:EnumCommodity'),
          defaultValue: commodities && enumEnumToValueNumber(EnumCommodity, commodities[0]),
          readOnly: commodities?.length === 1,
          clearable: true,
          inputSize: 4
        },
        {
          name: 'pxq',
          label: t('concepts:forms.add.inputs.pxq'),
          type: DataType.SWITCH,
          display: clientType === EnumClientTypes.DISTRIBUTOR ? true : false,
          inputSize: 4
          // hideForResponsive: true
        },
        {
          name: 'lgBypass',
          label: t('concepts:forms.add.inputs.lgBypass'),
          type: DataType.SWITCH,
          display: clientType === EnumClientTypes.DISTRIBUTOR ? true : false,
          inputSize: 4
        },
        {
          name: 'boolDescription',
          label: t('concepts:forms.add.inputs.boolDescription'),
          type: DataType.SWITCH,
          display: clientType === EnumClientTypes.DISTRIBUTOR ? true : false,
          inputSize: 4
        }
      ]
    }
  ]
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts

  useEffect(() => {
    fetchDataConceptTypes(clientType)
    fetchDataIVAtypes(clientType, conceptTypeSelected)

    const getLanguages = async () => {
      await fetchLanguages().then(response => {
        if (response.success) {
          setLanguages(mapLanguagesResponse(response.data))
        } else {
          error(t('concepts:forms.add.notifications.load.errorFetchingLanguages'))
          return []
        }
      })
    }
    getLanguages()
  }, [clientType, conceptTypeSelected])

  useEffect(() => {
    if (conceptTypesError) error(t('concepts:forms.add.notifications.load.errorConceptTypes'))
    if (IVAtypeError) error(t('concepts:forms.add.notifications.load.errorIVAtype'))
  }, [conceptTypesError, IVAtypeError])
  // #endregion Hooks Ends

  return (
    <Page layout={PageLayout.SINGLE_COLUMN} fullWidth={false}>
      <PageHeader title={t('concepts:components.ConceptsAddPage.title')}>
        <div>
          <Grid container direction="row" justify="flex-end" spacing={2}>
            <Grid item>
              <Button
                size="small"
                variant="text"
                color="primary"
                disabled={isFetching}
                onClick={() => {
                  history.goBack()
                }}
              >
                {t('concepts:mainActions.back')}
              </Button>
            </Grid>
            <Grid item>
              <Button
                size="small"
                variant="text"
                color="primary"
                disabled={isFetching}
                onClick={() => {
                  handleSaveAndRedirect(conceptData, () => {
                    history.push(NonAuthRoutes.Concepts, { clientType: clientType })
                  })
                }}
              >
                {t('concepts:mainActions.saveBack')}
              </Button>
            </Grid>
            <Grid item>
              <Button
                size="small"
                variant="contained"
                color="primary"
                disabled={isFetching}
                onClick={() => {
                  handleSaveAndRedirect(conceptData, newConceptId => {
                    history.push(NonAuthRoutes.ConceptsEdit, {
                      conceptId: newConceptId,
                      clientType: clientType,
                      calculationType: conceptData.calculationType
                    })
                  })
                }}
              >
                {t('concepts:mainActions.saveContinue')}
              </Button>
            </Grid>
          </Grid>
        </div>
      </PageHeader>
      <Card raised>
        <Form sections={addFormSections} data={conceptData} options={options} />
      </Card>
    </Page>
  )
}
